import React from 'react';
import { Box, Card, CardContent, Typography, Chip, keyframes } from '@mui/material';
import {
  ThumbUpAlt as ThumbUpIcon,
  ThumbDownAlt as ThumbDownIcon,
  LocalGasStation as StationIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { PollItemHighlight } from './types';

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 0px rgba(120,105,223,0); }
  50% { box-shadow: 0 0 18px rgba(120,105,223,0.18), 0 0 32px rgba(102,126,234,0.10); }
`;

const EASE_OUT: [number, number, number, number] = [0.25, 0.1, 0.25, 1];

interface Props {
  positive: PollItemHighlight[];
  negative: PollItemHighlight[];
  loading: boolean;
}

const SkeletonCard: React.FC = () => (
  <Card
    sx={{
      borderRadius: 3,
      overflow: 'hidden',
      background: 'linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%)',
      backgroundSize: '200% 100%',
      animation: `${shimmer} 1.5s infinite`,
      height: 360,
    }}
  />
);

// ─── Panel ────────────────────────────────────────────────────────────────
interface PanelProps {
  title: string;
  emptyText: string;
  items: PollItemHighlight[];
  baseColor: string;
  gradient: string;
  icon: React.ReactNode;
  limit?: number;
}

const Panel: React.FC<PanelProps> = ({ title, emptyText, items, baseColor, gradient, icon, limit = 8 }) => {
  const visible = items.slice(0, limit);

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
        <Box
          sx={{
            width: 28, height: 28, borderRadius: 1.5,
            background: gradient,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
            boxShadow: `0 3px 10px ${baseColor}30`,
          }}
        >
          {icon}
        </Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.9rem' }}>
          {title}
        </Typography>
      </Box>

      {visible.length === 0 ? (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: 4,
            color: '#94a3b8',
            fontSize: '0.85rem',
          }}
        >
          {emptyText}
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {visible.map((item, idx) => (
            <motion.div
              key={item.pollItemId}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: idx * 0.04, duration: 0.25, ease: EASE_OUT }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1,
                  py: 0.75,
                  borderRadius: 1.5,
                  border: `1px solid ${baseColor}20`,
                  bgcolor: `${baseColor}08`,
                  transition: 'background 0.15s, border-color 0.15s',
                  '&:hover': { bgcolor: `${baseColor}12`, borderColor: `${baseColor}40` },
                }}
              >
                {/* Ranking */}
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 800,
                    color: baseColor,
                    width: 18,
                    textAlign: 'right',
                    flexShrink: 0,
                    fontSize: '0.75rem',
                  }}
                >
                  {idx + 1}
                </Typography>

                {/* Type icon */}
                <Box sx={{ flexShrink: 0, color: '#94a3b8' }}>
                  {item.type === 'station'
                    ? <StationIcon sx={{ fontSize: 14 }} />
                    : <PersonIcon sx={{ fontSize: 14 }} />
                  }
                </Box>

                {/* Name */}
                <Typography
                  variant="body2"
                  sx={{
                    flex: 1,
                    fontWeight: 600,
                    color: '#1e293b',
                    fontSize: '0.85rem',
                    minWidth: 0,
                  }}
                  noWrap
                >
                  {item.name}
                </Typography>

                {/* Count + percent */}
                <Chip
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                      <Box component="span" sx={{ fontWeight: 800 }}>{item.count}</Box>
                      <Box component="span" sx={{ color: '#94a3b8', fontWeight: 500 }}>
                        ({item.percentOfTotal.toFixed(0)}%)
                      </Box>
                    </Box>
                  }
                  size="small"
                  sx={{
                    bgcolor: '#fff',
                    color: baseColor,
                    fontSize: '0.72rem',
                    height: 20,
                    border: `1px solid ${baseColor}30`,
                    '& .MuiChip-label': { px: 0.75, display: 'flex', alignItems: 'center' },
                  }}
                />
              </Box>
            </motion.div>
          ))}
        </Box>
      )}
    </Box>
  );
};

const PollItemsHighlights: React.FC<Props> = ({ positive, negative, loading }) => {
  if (loading) return <SkeletonCard />;

  if (!positive.length && !negative.length) {
    return (
      <Card sx={{ borderRadius: 3, p: 4, textAlign: 'center', border: '1px solid rgba(120,105,223,0.10)' }}>
        <Typography color="text.secondary" variant="body2">
          Nenhum cliente selecionou pollItems no período. Verifique se a rede tem pollItems cadastrados.
        </Typography>
      </Card>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: EASE_OUT }}
    >
      <Card
        sx={{
          borderRadius: 3,
          boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
          border: '1px solid rgba(120,105,223,0.10)',
          animation: `${pulseGlow} 4s ease-in-out infinite`,
        }}
      >
        <CardContent sx={{ pb: '16px !important' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#1e293b' }}>
              O que elogiam × O que reclamam
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mb: 2 }}>
            Tags selecionadas pelos clientes após dar a nota
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1px 1fr' },
              gap: 2,
            }}
          >
            <Panel
              title="O que elogiam"
              emptyText="Nenhum elogio selecionado"
              items={positive}
              baseColor="#22c55e"
              gradient="linear-gradient(135deg, #22c55e, #16a34a)"
              icon={<ThumbUpIcon sx={{ color: '#fff', fontSize: 14 }} />}
            />

            <Box
              sx={{
                width: 1,
                display: { xs: 'none', sm: 'block' },
                background: 'linear-gradient(180deg, transparent 0%, #e2e8f0 20%, #e2e8f0 80%, transparent 100%)',
              }}
            />

            <Panel
              title="O que reclamam"
              emptyText="Nenhuma crítica selecionada"
              items={negative}
              baseColor="#ef4444"
              gradient="linear-gradient(135deg, #ef4444, #dc2626)"
              icon={<ThumbDownIcon sx={{ color: '#fff', fontSize: 14 }} />}
            />
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default PollItemsHighlights;
